/* -----------------Globals--------------- */
import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";

import type { IconType } from "../TextLoop";
/* -----------------Types--------------- */
import type { FeedbackParams } from "./types";

/* -----------------Child components--------------- */
import Box from "../Box";
import { useToast } from "../Toast";
import { FeedbackForm } from "./FeedbackForm";
import { FeedbackHeader } from "./FeedbackHeader";
import { FeedbackReasons } from "./FeedbackReasons";

type FeedbackContentRef = {
	submit: () => void;
};

type FeedbackContentProps = {
	sendFeedback: (params: FeedbackParams, feedback: string) => void;
	feedbackDialogParams: FeedbackParams;
	onDismiss: () => void;
	subtitle: string;
	subtitleSuffix?: string;
	showFeedbackConfirmationToast: () => void;
	handlePostFeedback: (
		feedback: string,
		email: string,
		selectedReason?: string,
	) => void;
	feedbackReasonList?: {
		title: string;
		description: string;
		icon: IconType;
		value: string;
	}[];
	onStateChange?: (sendingFeedback: boolean, isValid: boolean) => void;
	feedbackLimitError?: string;
	feedbackPlaceholder?: string;
};

type FeedbackContentComponent = React.ForwardRefExoticComponent<
	FeedbackContentProps & React.RefAttributes<FeedbackContentRef>
> & {
	Header: typeof FeedbackHeader;
	Reasons: typeof FeedbackReasons;
	Form: typeof FeedbackForm;
};

const FeedbackContent = forwardRef<FeedbackContentRef, FeedbackContentProps>(
	(
		{
			sendFeedback,
			feedbackDialogParams,
			onDismiss,
			showFeedbackConfirmationToast,
			handlePostFeedback,
			feedbackReasonList,
			onStateChange,
			feedbackLimitError,
			feedbackPlaceholder,
		},
		ref,
	) => {
		const { showToast } = useToast();
		const [sendingFeedback, setSendingFeedback] = useState(false);
		const [selectedReason, setSelectedReason] = useState<string | null>(null);
		const [isEmailValid, setIsEmailValid] = useState(true);
		const [isFeedbackValid, setIsFeedbackValid] = useState(false);
		const [emailText, setEmailText] = useState("");
		const [feedbackText, setFeedbackText] = useState("");
		const formRef = useRef<{ submit: () => void } | null>(null);

		useImperativeHandle(ref, () => ({
			submit: () => {
				if (formRef.current) {
					formRef.current.submit();
				}
			},
		}));

		useEffect(() => {
			if (onStateChange) {
				const isEmailRequired = !feedbackDialogParams?.email;
				const emailValidationPassed =
					!isEmailRequired || (isEmailValid && emailText.length > 0);

				const feedbackValidationPassed =
					feedbackReasonList && feedbackReasonList.length > 0
						? !!selectedReason
						: isFeedbackValid && feedbackText.length > 0;

				onStateChange(
					sendingFeedback,
					!sendingFeedback && emailValidationPassed && feedbackValidationPassed,
				);
			}
		}, [
			sendingFeedback,
			selectedReason,
			isEmailValid,
			isFeedbackValid,
			emailText,
			feedbackText,
			onStateChange,
			feedbackReasonList,
			feedbackDialogParams?.email,
		]);

		const handleSendFeedback = async (feedback: string, email: string) => {
			setSendingFeedback(true);

			onDismiss();
			showFeedbackConfirmationToast();
			handlePostFeedback(
				feedback,
				feedbackDialogParams.email ?? email,
				selectedReason || undefined,
			);

			try {
				const feedbackMessage = selectedReason
					? `Selected reason: ${selectedReason} \nUser feedback: ${feedback}`
					: `User feedback: ${feedback}`;
				const finalFeedbackDialogParams = {
					...feedbackDialogParams,
					feedback: feedbackMessage,
					email: feedbackDialogParams.email || email,
				};

				await sendFeedback(finalFeedbackDialogParams, feedbackMessage);
			} catch {
				showToast({
					message: "Failed to send feedback. Please try again.",
					action: {
						label: "Retry",
						action: () => {
							handleSendFeedback(feedback, email);
						},
					},
				});
			} finally {
				setSendingFeedback(false);
			}
		};

		const handleReasonPress = (reason: string) => {
			setSelectedReason(selectedReason === reason ? null : reason);
		};

		return (
			<Box gap={16}>
				{feedbackReasonList && feedbackReasonList.length > 0 && (
					<FeedbackReasons
						feedbackReasonList={feedbackReasonList}
						selectedReason={selectedReason}
						onReasonPress={handleReasonPress}
					/>
				)}
				<Box padding={16}>
					<FeedbackForm
						ref={formRef}
						feedbackLimitError={feedbackLimitError ?? ""}
						feedbackPlaceholder={feedbackPlaceholder ?? ""}
						feedbackDialogParams={feedbackDialogParams}
						feedbackReasonList={feedbackReasonList}
						onSubmit={handleSendFeedback}
						onEmailValidationChange={setIsEmailValid}
						onFeedbackValidationChange={setIsFeedbackValid}
						onEmailTextChange={setEmailText}
						onFeedbackTextChange={setFeedbackText}
					/>
				</Box>
			</Box>
		);
	},
);

FeedbackContent.displayName = "FeedbackContent";

const FeedbackContentWithComposition =
	FeedbackContent as FeedbackContentComponent;
FeedbackContentWithComposition.Header = FeedbackHeader;
FeedbackContentWithComposition.Reasons = FeedbackReasons;
FeedbackContentWithComposition.Form = FeedbackForm;

export { FeedbackContentWithComposition as FeedbackContent };
